import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import GroupStatusStack from './components/GroupStatusStack';
import DocumentViewer from './components/documentViewer';
import { OrderStatusPanelData, useOrderStatusPanelQuery } from './helpers/useOrderStatusPanelQueries';
import { UIStatusGroup } from './types';
import {
  AttachmentTypeDto,
  SalesOrderListDto,
  SalesOrderStatusGroupDto,
  SalesOrderStatusHistoryDto
} from 'src/models/salesorder';
import VigoSpinner from '../vigo-spinner';
import StatusEta from './components/StatusEta';
import StatusPanelHeader from './components/StatusPanelHeader';
import {
  combineGroupsAndStatuses,
  filterAndSortGroups,
  filterEmptyDiscrepancies,
  getUiGroupsFromStatusGroups,
  groupStatusByDateThenStatusGroupId
} from './helpers';
import { AttachmentDto } from 'src/models/attachment';
import OrphanConsignmentDocuments from './components/OrphanConsignmentDocuments';
import PodLocationMap from './components/PodLocationMap';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import FlowPodSection from './components/FlowPodSection';
import FlowStatusGrid from './components/FlowStatusGrid';
import { VigoButton } from '../vigo-button';

export interface StatusPanelItems {
  id?: string;
  itemNo?: number;
}

export interface VigoOrderStatusPanelProps {
  orderId?: string;
  partnerId?: string;
  statusDataOverride?: SalesOrderStatusHistoryDto[];
  statusGroupsOverride?: SalesOrderStatusGroupDto[];
  attachmentTypesOverride?: AttachmentTypeDto[];
  useOverride: boolean;
  orderIdHash?: string;
  etaWindow?: string;
  liveEta?: string;
  orderItems?: StatusPanelItems[];
  isDelivered: boolean;
  isDeleted?: boolean;
  deliveryCountryCode?: string;
  salesOrderSummary?: Pick<SalesOrderListDto, 'deliveryCountryCode' | 'orderNumber'>;

  /**
   * Use this to force the View to a specific product level (Flow or MyPortal).
   */
  productOverride?: Products;
}

export interface FlowStatusGroupDisplay {
  itemNo: number;
  statuses: StatusItem[];
}

export interface StatusItem {
  code: string;
  description: string;
  date: string;
  notes?: string;
  podName?: string;
  latitude?: number;
  longitude?: number;
  image?: { id: string; type: string };
  itemNo?: number;
}

const VigoOrderStatusPanel = ({
  orderId = '',
  partnerId = '',
  statusDataOverride,
  statusGroupsOverride,
  attachmentTypesOverride,
  useOverride,
  orderIdHash,
  etaWindow,
  liveEta,
  orderItems,
  isDelivered,
  isDeleted,
  deliveryCountryCode,
  salesOrderSummary,
  productOverride
}: VigoOrderStatusPanelProps) => {
  // State
  const [details, setDetails] = useState<OrderStatusPanelData>();
  const [flowDataSource, setFlowDataSource] = useState<FlowStatusGroupDisplay[]>([]);
  const [myPortalDataSource, setMyPortalDataSource] = useState<UIStatusGroup[]>([]);
  const [podInfo, setPodInfo] = useState<StatusItem>();
  const [docViewerOpen, setDocViewerOpen] = useState<boolean>(false);
  const [docsWithoutStatus, setDocsWithoutStatus] = useState<AttachmentDto[]>([]);
  const [currentlyActiveStatusId, setCurrentlyActiveStatusId] = useState<string>('');

  const attachments = useMemo(() => {
    return useOverride ? details?.attachmentsOverride ?? [] : details?.attachments ?? [];
  }, [details?.attachments, details?.attachmentsOverride, useOverride]);

  // Hooks
  const prod = useGetProductLevel();
  const prodFinal = useMemo((): Products => {
    let p = prod;
    if (productOverride !== undefined) {
      p = productOverride;
    }

    return p;
  }, [prod, productOverride]);

  const getAttachmentTypeId = useCallback(
    (type: 'PROOF_OF_DELIVERY' | 'STATUS_IMAGE', attachmentTypes: AttachmentTypeDto[]): string => {
      switch (type) {
        case 'PROOF_OF_DELIVERY': {
          if (attachmentTypesOverride) {
            return attachmentTypesOverride.find(type => type.isPod)?.id ?? '';
          } else if (attachmentTypes) {
            return attachmentTypes.find(type => type.isPod)?.id ?? '';
          } else {
            return '';
          }
        }
        case 'STATUS_IMAGE': {
          if (attachmentTypesOverride) {
            return attachmentTypesOverride.find(type => type.isStatusImage)?.id ?? '';
          } else if (attachmentTypes) {
            return attachmentTypes.find(type => type.isStatusImage)?.id ?? '';
          } else {
            return '';
          }
        }
        default: {
          return '';
        }
      }
    },
    [attachmentTypesOverride]
  );

  const statusPanelDataOnSuccess = async (data: OrderStatusPanelData) => {
    setDetails(data);

    if (prodFinal === Products.Flow) {
      // Build FLOW data source
      const groups: FlowStatusGroupDisplay[] = [];

      const jobLevelStatuses = data.statusHistory?.filter(x => x.salesOrderItemId === undefined) ?? [];
      const itemLevelStatuses = data.statusHistory?.filter(x => x.salesOrderItemId !== undefined) ?? [];

      if (orderItems) {
        for (let index = 0; index < orderItems.length; index++) {
          const group: FlowStatusGroupDisplay = { itemNo: orderItems[index].itemNo ?? 0, statuses: [] };

          // Add job level statuses to each item
          for (let j = 0; j < jobLevelStatuses.length; j++) {
            const status = jobLevelStatuses[j];

            group.statuses.push({
              code: status.salesOrderStatus?.code ?? '',
              description: status.salesOrderStatus?.description ?? '',
              date: status.statusDate ?? '',
              notes: status.notes,
              podName: status.podName,
              latitude: status.latitude,
              longitude: status.longitude
            });
          }

          // Add item level statuses for this item
          const itemStatuses = itemLevelStatuses.filter(
            x => x.salesOrderItemId?.toLowerCase() === orderItems[index].id?.toLowerCase()
          );

          for (let k = 0; k < itemStatuses.length; k++) {
            const status = itemStatuses[k];

            group.statuses.push({
              code: status.salesOrderStatus?.code ?? '',
              description: status.salesOrderStatus?.description ?? '',
              notes: status.notes,
              date: status.statusDate ?? '',
              podName: status.podName,
              latitude: status.latitude,
              longitude: status.longitude
            });
          }

          // Sort statuses by date (desc)
          group.statuses.sort((a, b) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });

          groups.push(group);
        }
      }

      // Sort groups by itemNo
      groups.sort((a, b) => a.itemNo - b.itemNo);

      if (isDelivered) {
        // Set POD info
        const deliveredStatus = data.statusHistory?.find(
          x =>
            x.salesOrderStatus?.salesOrderStatusGroup?.code?.toLowerCase() === 'delivered' &&
            x.podName &&
            x.podName.trim() !== ''
        );
        const podImageType = data.attachmentTypes?.find(x => x.isPod);
        const podImage = data.attachments?.find(x => x.attachmentTypeId === podImageType?.id);
        if (deliveredStatus) {
          setPodInfo({
            code: deliveredStatus.salesOrderStatus?.code ?? '',
            description: deliveredStatus.salesOrderStatus?.description ?? '',
            date: deliveredStatus.statusDate ?? '',
            notes: deliveredStatus.notes,
            podName: deliveredStatus.podName,
            latitude: deliveredStatus.latitude,
            longitude: deliveredStatus.longitude,
            image: podImage ? { id: podImage.id ?? '', type: podImage.fileType ?? '' } : undefined
          });
        }
      }

      setFlowDataSource(groups);
    } else if (prodFinal === Products.MyPortal) {
      // Build MyPortal data source

      const distinctStatusGroupIds: string[] = [];
      if (!useOverride) {
        // Loop through all status codes and add distinct status group IDs to an array
        for (let index = 0; index < (data.allStatuses?.length ?? 0); index++) {
          const status = data.allStatuses?.[index];
          if (!distinctStatusGroupIds.includes(status?.salesOrderStatusGroup?.id ?? '')) {
            distinctStatusGroupIds.push(status?.salesOrderStatusGroup?.id ?? '');
          }
        }
      }

      const groupedStatus = groupStatusByDateThenStatusGroupId(
        statusDataOverride ? statusDataOverride : data.statusHistory ?? []
      );
      const uiGroups = getUiGroupsFromStatusGroups(
        statusGroupsOverride ? statusGroupsOverride : data.statusGroups ?? []
      );

      // Filter uiGroups to only those that are in distinctStatusGroupIds
      let filteredUiGroups: UIStatusGroup[] = [];
      if (useOverride) {
        // These should already be filtered via statusGroupsOverride
        filteredUiGroups.push(...uiGroups);
      } else {
        filteredUiGroups = uiGroups.filter(group => distinctStatusGroupIds.includes(group.id));
      }

      const delCountryCode =
        salesOrderSummary
          ? salesOrderSummary.deliveryCountryCode ?? ''
          : useOverride && Boolean(deliveryCountryCode)
          ? deliveryCountryCode ?? ''
          : data.salesOrder?.lines?.find(line => line.salesOrderLineTypeId === '97285b43-5465-452d-9b30-47cf3be65a71')
              ?.address?.countryIso3 ?? '';

      const acceptedAttachmentTypeIds = [
        getAttachmentTypeId(
          'PROOF_OF_DELIVERY',
          useOverride ? attachmentTypesOverride ?? [] : data.attachmentTypes ?? []
        ),
        getAttachmentTypeId('STATUS_IMAGE', useOverride ? attachmentTypesOverride ?? [] : data.attachmentTypes ?? [])
      ];

      // Set docsWithoutStatus to attachments with no metadata OR metadata that has blank or empty 'SalesOrderStatus' or 'SalesOrderStatusId'
      const docsNoStatus = (useOverride ? data.attachmentsOverride ?? [] : data.attachments ?? []).filter(
        attachment => {
          if (!attachment.metadata || attachment.metadata.length === 0) {
            return true;
          }

          const statusMeta = attachment.metadata.find(
            meta => meta.key === 'SalesOrderStatus' || meta.key === 'SalesOrderStatusId'
          );
          if (!statusMeta || !statusMeta.value || statusMeta.value.trim() === '') {
            return true;
          }

          return false;
        }
      );
      setDocsWithoutStatus(docsNoStatus);

      const attachments: AttachmentDto[] = [];
      // Filter attachments that aren't in docsWithoutStatus
      (useOverride ? data.attachmentsOverride ?? [] : data.attachments ?? []).forEach(attachment => {
        if (!docsNoStatus.find(doc => doc.id === attachment.id)) {
          attachments.push(attachment);
        }
      });

      const combinedGroupsAndStatuses = combineGroupsAndStatuses(
        filteredUiGroups,
        groupedStatus,
        attachments,
        acceptedAttachmentTypeIds,
        delCountryCode
      );
      const filtered = filterAndSortGroups(combinedGroupsAndStatuses, filteredUiGroups).filter(x => x.id);
      const filteredWithoutEmptyDiscrepancies = filterEmptyDiscrepancies(filtered);
      setCurrentlyActiveStatusId(filteredWithoutEmptyDiscrepancies.find(x => x.items.length > 0)?.id ?? '');
      setMyPortalDataSource(filteredWithoutEmptyDiscrepancies);
    }
  };

  /** REACT QUERY */
  const { isFetching, refetch } = useOrderStatusPanelQuery(
    orderId,
    partnerId,
    isDeleted ?? false,
    useOverride,
    orderIdHash,
    prodFinal,
    {
      onSuccess: data => statusPanelDataOnSuccess(data)
    },
    salesOrderSummary
  );

  useEffect(() => {
    // Reset state on orderId change
    setDetails(undefined);
    setFlowDataSource([]);
    setMyPortalDataSource([]);
    setPodInfo(undefined);
    setDocViewerOpen(false);
    setDocsWithoutStatus([]);
    setCurrentlyActiveStatusId('');
  }, [orderId]);

  if (isFetching) {
    return (
      <Box sx={{ width: '100%', height: '50vh', display: 'grid', placeContent: 'center' }}>
        <VigoSpinner name="statusPanelSpinner" />
      </Box>
    );
  }

  return (
    <>
      {/* If prod is FLOW, we want to show all statuses (not groups) */}
      {prodFinal === Products.Flow && (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 4 }}>
          {/* ETA Section */}
          {liveEta && !isDelivered && (
            <Box sx={{ flexShrink: 0, mb: 2 }}>
              <StatusEta {...{ liveEta, etaWindow }} />
            </Box>
          )}

          {/* POD */}
          {podInfo && <FlowPodSection {...{ podInfo, setDocViewerOpen }} />}

          {Boolean(attachments.length) && (
            <VigoButton
              name="showImgBtn"
              value="Show Images"
              sx={theme => ({
                textTransform: 'none !important',
                width: '30%',
                ...(theme.palette.mode === 'dark' && {
                  color: theme.palette.common.white,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                })
              })}
              variant="outlined"
              onClick={() => {
                setDocViewerOpen(true);
              }}
            />
          )}

          <FlowStatusGrid dataSource={flowDataSource} {...{ setDocViewerOpen }} />
        </Box>
      )}

      {prodFinal === Products.MyPortal && (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 4 }}>
          <StatusPanelHeader loading={isFetching} />
          {liveEta && !isDelivered && <StatusEta {...{ liveEta, etaWindow }} />}

          <Stack direction="column" gap={1} sx={{ flex: 1, overflowY: 'auto' }}>
            <GroupStatusStack
              uiStatusGroups={myPortalDataSource}
              setDocumentViewerOpen={setDocViewerOpen}
              {...{ orderItems, currentlyActiveStatusId }}
            />

            <OrphanConsignmentDocuments {...{ docsWithoutStatus, setDocViewerOpen }} />
            <PodLocationMap key={orderId} statusGroups={myPortalDataSource} sx={{ marginTop: '20px' }} />
          </Stack>
        </Box>
      )}

      <DocumentViewer
        documents={useOverride ? details?.attachmentsOverride ?? [] : details?.attachments ?? []}
        statuses={useOverride ? statusDataOverride ?? [] : details?.statusHistory ?? []}
        open={!!docViewerOpen}
        handleClose={() => setDocViewerOpen(false)}
        consignmentNumber={salesOrderSummary?.orderNumber ?? details?.salesOrder?.orderNumber ?? ''}
        messagingService={details?.messagingService}
        refetchCallback={refetch}
        {...{ useOverride, orderId, orderItems }}
      />
    </>
  );
};

export default VigoOrderStatusPanel;
